import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

interface MentorApplication {
  id: string;
  user_id: string | null;
  full_name: string;
  email: string;
  sport: string;
  career_background: string | null;
  linkedin_url: string | null;
  status: string;
  created_at: string;
}

interface Mentor {
  id: string;
  user_id: string | null;
  full_name: string;
  sport: string;
  role_title: string | null;
  company: string | null;
  bio: string;
  linkedin_url: string | null;
  mentor_status: string;
  is_active: boolean;
  created_at: string;
}

function formatDate(ts: string) {
  return new Date(ts).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
}

function SectionHeader({ title, count, color = '#e88c0e' }: { title: string; count: number; color?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
      <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700, fontSize: 16, color: 'var(--text)', textTransform: 'uppercase' as const, letterSpacing: 1 }}>
        {title}
      </div>
      {count > 0 && (
        <span style={{ padding: '2px 8px', borderRadius: 10, background: color + '22', color, fontSize: 11, fontWeight: 700 }}>
          {count}
        </span>
      )}
    </div>
  );
}

function ApplicationCard({ app, onStatusChange }: { app: MentorApplication; onStatusChange: (id: string, status: string) => void }) {
  const initials = app.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  return (
    <div style={{ background: 'var(--surface)', borderRadius: 12, border: '1px solid var(--border)', padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
        <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#014c8d25', border: '2px solid #014c8d40', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, color: '#5b9bd5', flexShrink: 0 }}>
          {initials}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text)', lineHeight: 1.2 }}>{app.full_name}</div>
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>
            <a href={`mailto:${app.email}`} style={{ color: '#5b9bd5', textDecoration: 'none' }}>{app.email}</a>
          </div>
          <span style={{ display: 'inline-block', marginTop: 5, padding: '2px 8px', borderRadius: 10, background: '#014c8d20', color: '#5b9bd5', fontSize: 10, fontWeight: 700 }}>
            {app.sport}
          </span>
        </div>
        <div style={{ fontSize: 10, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>{formatDate(app.created_at)}</div>
      </div>

      {app.career_background && (
        <div style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.6, marginBottom: 14, background: 'var(--surface-2)', borderRadius: 8, padding: '10px 12px' }}>
          {app.career_background}
        </div>
      )}

      {app.linkedin_url && (
        <div style={{ fontSize: 12, marginBottom: 14 }}>
          <a href={app.linkedin_url} target="_blank" rel="noopener noreferrer" style={{ color: '#5b9bd5', textDecoration: 'none' }}>
            LinkedIn ↗
          </a>
        </div>
      )}

      {app.status === 'pending' && (
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => onStatusChange(app.id, 'approved')}
            style={{ flex: 1, padding: '9px 0', borderRadius: 8, border: 'none', background: '#0fa86d', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
          >
            ✓ Approve & Invite
          </button>
          <button
            onClick={() => onStatusChange(app.id, 'rejected')}
            style={{ flex: 1, padding: '9px 0', borderRadius: 8, border: '1px solid #bf1e2e', background: 'transparent', color: '#bf1e2e', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
          >
            ✕ Decline
          </button>
        </div>
      )}
      {app.status !== 'pending' && (
        <div style={{ fontSize: 12, color: app.status === 'approved' ? '#0fa86d' : '#bf1e2e', fontWeight: 700, textTransform: 'uppercase' as const }}>
          {app.status}
        </div>
      )}
    </div>
  );
}

function MentorCard({ mentor, onApprove, onReject }: { mentor: Mentor; onApprove?: (id: string) => void; onReject?: (id: string) => void }) {
  const initials = mentor.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  return (
    <div style={{ background: 'var(--surface)', borderRadius: 12, border: '1px solid var(--border)', padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
        <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#bf1e2e25', border: '2px solid #bf1e2e40', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, color: '#bf1e2e', flexShrink: 0 }}>
          {initials}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text)', lineHeight: 1.2 }}>{mentor.full_name}</div>
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>
            {mentor.role_title}{mentor.company ? ` · ${mentor.company}` : ''}
          </div>
          <span style={{ display: 'inline-block', marginTop: 5, padding: '2px 8px', borderRadius: 10, background: '#bf1e2e20', color: '#bf1e2e', fontSize: 10, fontWeight: 700 }}>
            {mentor.sport}
          </span>
        </div>
        <div style={{ fontSize: 10, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>{formatDate(mentor.created_at)}</div>
      </div>

      {mentor.bio && (
        <div style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.6, marginBottom: 14, background: 'var(--surface-2)', borderRadius: 8, padding: '10px 12px' }}>
          {mentor.bio}
        </div>
      )}

      {mentor.linkedin_url && (
        <div style={{ fontSize: 12, marginBottom: 14 }}>
          <a href={mentor.linkedin_url} target="_blank" rel="noopener noreferrer" style={{ color: '#5b9bd5', textDecoration: 'none' }}>
            LinkedIn ↗
          </a>
        </div>
      )}

      {onApprove && onReject && (
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => onApprove(mentor.id)} style={{ flex: 1, padding: '9px 0', borderRadius: 8, border: 'none', background: '#0fa86d', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
            ✓ Approve
          </button>
          <button onClick={() => onReject(mentor.id)} style={{ flex: 1, padding: '9px 0', borderRadius: 8, border: '1px solid #bf1e2e', background: 'transparent', color: '#bf1e2e', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
            ✕ Reject
          </button>
        </div>
      )}
    </div>
  );
}

export default function MentorsPage() {
  const [applications, setApplications] = useState<MentorApplication[]>([]);
  const [pendingMentors, setPendingMentors] = useState<Mentor[]>([]);
  const [approvedMentors, setApprovedMentors] = useState<Mentor[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  const showToast = (msg: string, ok: boolean) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3500);
  };

  const load = async () => {
    setLoading(true);
    const [{ data: apps }, { data: mentors }] = await Promise.all([
      supabase.from('mentor_applications').select('*').order('created_at', { ascending: false }),
      supabase.from('mentors').select('*').order('created_at', { ascending: false }),
    ]);
    setApplications((apps as MentorApplication[]) || []);
    const ms = (mentors as Mentor[]) || [];
    setPendingMentors(ms.filter(m => m.mentor_status === 'pending'));
    setApprovedMentors(ms.filter(m => m.mentor_status === 'approved'));
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const sendPushNotification = async (userId: string, title: string, body: string) => {
    try {
      const { data: profile } = await supabase
        .from('profiles').select('push_token').eq('id', userId).single();
      if (!profile?.push_token) return;
      await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/push-notification-sender`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ to: profile.push_token, title, body }),
        }
      );
    } catch (_) {}
  };

  const handleApplicationStatus = async (id: string, status: string) => {
    const app = applications.find(a => a.id === id);
    const { error } = await supabase.from('mentor_applications').update({ status }).eq('id', id);
    if (error) {
      showToast('Failed to update application. Please try again.', false);
      return;
    }
    if (status === 'approved' && app) {
      const { data: insertedMentor, error: insertError } = await supabase.from('mentors').insert({
        user_id: app.user_id || null,
        full_name: app.full_name,
        sport: app.sport,
        bio: app.career_background || '',
        linkedin_url: app.linkedin_url || null,
        mentor_status: 'approved',
        is_active: true,
      }).select('id').single();
      if (insertError) {
        // Roll back the application status update so it stays pending
        await supabase.from('mentor_applications').update({ status: 'pending' }).eq('id', id);
        showToast(`Approval failed (mentors insert): ${insertError.message} — no changes saved.`, false);
        load();
        return;
      }
      if (app.user_id) {
        const { error: profileError } = await supabase
          .from('profiles')
          .update({ user_type: 'mentor' })
          .eq('id', app.user_id);
        if (profileError) {
          // Roll back both previous steps
          await supabase.from('mentors').delete().eq('id', insertedMentor.id);
          await supabase.from('mentor_applications').update({ status: 'pending' }).eq('id', id);
          showToast(`Approval failed (profile update): ${profileError.message} — all changes rolled back.`, false);
          load();
          return;
        }
      }
      // Send approval email — failure here is non-fatal, approval already committed
      try {
        await fetch(
          `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-mentor-approval`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ to_email: app.email, to_name: app.full_name }),
          }
        );
      } catch (_) {}
    }
    showToast(status === 'approved' ? 'Application approved — email sent.' : 'Application declined.', status === 'approved');
    load();
  };

  const handleApproveMentor = async (id: string) => {
    const mentor = pendingMentors.find(m => m.id === id);
    const { error } = await supabase.from('mentors').update({ mentor_status: 'approved', is_active: true }).eq('id', id);
    if (error) {
      showToast('Failed to approve mentor. Please try again.', false);
      return;
    }
    if (mentor?.user_id) {
      await supabase.from('profiles').update({ user_type: 'mentor' }).eq('id', mentor.user_id);
      await sendPushNotification(
        mentor.user_id,
        'You\'ve been approved as a BTHD Mentor',
        'Your mentor application has been approved. You can now connect with athletes on the platform.'
      );
    }
    showToast('Mentor approved.', true);
    load();
  };

  const handleRejectMentor = async (id: string) => {
    const { error } = await supabase.from('mentors').update({ mentor_status: 'rejected', is_active: false }).eq('id', id);
    if (error) {
      showToast('Failed to reject mentor. Please try again.', false);
      return;
    }
    showToast('Mentor declined.', false);
    load();
  };

  const pendingApps = applications.filter(a => a.status === 'pending');
  const totalPending = pendingApps.length + pendingMentors.length;

  return (
    <div>
      {toast && (
        <div style={{
          position: 'fixed', top: 24, right: 24, zIndex: 1000,
          background: toast.ok ? '#0fa86d' : '#bf1e2e',
          color: '#fff', borderRadius: 10, padding: '12px 20px',
          fontWeight: 700, fontSize: 14, boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          {toast.ok ? '✓' : '✕'} {toast.msg}
        </div>
      )}
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 800, fontSize: 28, color: 'var(--text)' }}>Mentors</div>
        <div style={{ fontSize: 13, color: 'var(--text-3)', marginTop: 4 }}>
          {loading ? 'Loading...' : `${totalPending} pending · ${approvedMentors.length} approved`}
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-3)' }}>Loading...</div>
      ) : (
        <>
          {/* Public Applications (from mentor-apply screen) */}
          <div style={{ marginBottom: 36 }}>
            <SectionHeader title="New Applications" count={pendingApps.length} color="#014c8d" />
            {pendingApps.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px 24px', color: 'var(--text-3)', background: 'var(--surface)', borderRadius: 12, border: '1px solid var(--border)' }}>
                No new applications
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
                {pendingApps.map(a => (
                  <ApplicationCard key={a.id} app={a} onStatusChange={handleApplicationStatus} />
                ))}
              </div>
            )}
          </div>

          {/* In-App Pending (from authenticated users) */}
          {pendingMentors.length > 0 && (
            <div style={{ marginBottom: 36 }}>
              <SectionHeader title="In-App Applications" count={pendingMentors.length} color="#e88c0e" />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
                {pendingMentors.map(m => (
                  <MentorCard key={m.id} mentor={m} onApprove={handleApproveMentor} onReject={handleRejectMentor} />
                ))}
              </div>
            </div>
          )}

          {/* Approved Mentors */}
          <div>
            <SectionHeader title="Approved Mentors" count={approvedMentors.length} color="#0fa86d" />
            {approvedMentors.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px 24px', color: 'var(--text-3)', background: 'var(--surface)', borderRadius: 12, border: '1px solid var(--border)' }}>
                No approved mentors yet
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
                {approvedMentors.map(m => <MentorCard key={m.id} mentor={m} />)}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
